import * as React from "react";

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";
import { toast } from "sonner";
import { HelpCircle, Save } from "lucide-react";
import { FaRegCopy } from "react-icons/fa";
import { Checkbox } from "./ui/checkbox";
import { useCreateRequest } from "../../hooks/useCreateRequest";
import { useClipboard } from "../../hooks/useClipboard";

interface RelatedQuestionsProps {
  questions: string[];
  onQuestionClick: (question: string) => void;
  requestType: "discuss" | "research";
}

interface QuestionActionItemProps {
  icon: React.ReactNode;
  tooltipContent: string;
  onClick: () => void;
}

const QuestionActionItem: React.FC<QuestionActionItemProps> = ({
  icon,
  tooltipContent,
  onClick,
}) => {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div role="button" onClick={onClick}>
          {icon}
        </div>
      </TooltipTrigger>
      <TooltipContent>{tooltipContent}</TooltipContent>
    </Tooltip>
  );
};

const RelatedQuestions: React.FC<RelatedQuestionsProps> = ({
  questions,
  onQuestionClick,
  requestType,
}) => {
  const [selectedQuestions, setSelectedQuestions] = React.useState<Set<string>>(new Set());
  const { mutateAsync: createRequestMutation } = useCreateRequest();
  const { copyToClipboard } = useClipboard();

  const handleToggleSelectAll = () => {
    if (selectedQuestions.size === questions.length) {
      setSelectedQuestions(new Set());
    } else {
      setSelectedQuestions(new Set(questions));
    }
  };

  const handleToggleQuestionSelection = (question: string) => {
    const newSelected = new Set(selectedQuestions);
    if (newSelected.has(question)) {
      newSelected.delete(question);
    } else {
      newSelected.add(question);
    }
    setSelectedQuestions(newSelected);
  };

  const handleCopyAllQuestions = async () => {
    const allQuestionsText = questions.join("\n");
    await copyToClipboard(
      allQuestionsText,
      `All ${questions.length} questions copied to clipboard`
    );
  };

  const handleCopyQuestion = async (question: string) => {
    await copyToClipboard(question, "Question copied to clipboard");
  };

  const handleSaveQuestion = async (question: string) => {
    try {
      const requestName = question.length > 50 ? question.substring(0, 50) + "..." : question;

      await createRequestMutation({
        type: requestType,
        name: requestName,
        content: question,
        category_id: null,
        chain_with: [],
        groups: [],
      });
    } catch (error) {
      console.error("Failed to save question to request library:", error);
    }
  };

  const handleSaveSelectedQuestions = async () => {
    if (selectedQuestions.size === 0) {
      toast.warning("No Selection", {
        description: "Please select at least one question",
      });
      return;
    }

    try {
      const selectedQuestionsArray = Array.from(selectedQuestions);

      const savePromises = selectedQuestionsArray.map((question) => {
        const requestName = question.length > 50 ? question.substring(0, 50) + "..." : question;

        return createRequestMutation({
          type: requestType,
          name: requestName,
          content: question,
          category_id: null,
          chain_with: [],
          groups: [],
        });
      });

      await Promise.all(savePromises);

      setSelectedQuestions(new Set());
    } catch (error) {
      console.error("Failed to save selected questions to request library:", error);
    }
  };

  return (
    <TooltipProvider>
      <div>
        <div className="flex items-center justify-between mb-3">
          <h4 className="flex items-center gap-2 text-base font-medium">
            Related Questions
            <HelpCircle className="size-3.5" />
          </h4>
          <div className="flex items-center gap-2">
            <QuestionActionItem
              icon={<FaRegCopy className="size-3.5" />}
              tooltipContent="Copy all questions"
              onClick={handleCopyAllQuestions}
            />
            <QuestionActionItem
              icon={<Save className="size-3.5" />}
              tooltipContent="Save selected questions to request library"
              onClick={handleSaveSelectedQuestions}
            />
            <QuestionActionItem
              icon={
                <Checkbox
                  checked={selectedQuestions.size === questions.length}
                  onCheckedChange={handleToggleSelectAll}
                  aria-label="Select all questions"
                  className="bg-neutral-white border-neutral-500"
                />
              }
              tooltipContent="Select all questions"
              onClick={handleToggleSelectAll}
            />
          </div>
        </div>

        <div className="flex flex-col gap-y-2">
          {questions.map((question, qIndex) => (
            <Tooltip key={qIndex}>
              <TooltipTrigger asChild>
                <div
                  className="flex items-start gap-2 justify-between bg-primary-100 rounded-lg py-3 px-4 relative cursor-pointer hover:bg-primary-200 border border-transparent hover:border-primary-300"
                  onClick={() => onQuestionClick(question)}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-normal text-left whitespace-normal text-primary-300">
                      {question}
                    </p>
                  </div>
                  <div
                    className="flex flex-col items-center gap-2"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <QuestionActionItem
                      icon={
                        <Checkbox
                          checked={selectedQuestions.has(question)}
                          onCheckedChange={() => handleToggleQuestionSelection(question)}
                          aria-label="Select question"
                          className="bg-neutral-white border-neutral-500"
                        />
                      }
                      tooltipContent="Select question"
                      onClick={() => handleToggleQuestionSelection(question)}
                    />
                    <QuestionActionItem
                      icon={<Save className="size-3.5" />}
                      tooltipContent="Save to request library"
                      onClick={() => handleSaveQuestion(question)}
                    />
                    <QuestionActionItem
                      icon={<FaRegCopy className="size-3.5" />}
                      tooltipContent="Copy question"
                      onClick={() => handleCopyQuestion(question)}
                    />
                  </div>
                </div>
              </TooltipTrigger>
              <TooltipContent>Click to send</TooltipContent>
            </Tooltip>
          ))}
        </div>
      </div>
    </TooltipProvider>
  );
};

export default RelatedQuestions;
